// LoginNew.tsx - Updated with HealthGo theme
import React from "react";
import "./login.scss";
import {
  Form,
  Field,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import FormPasswordField from "../../components/formFields/FormPasswordField";
import {
  passwordValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import { useNavigate } from "react-router-dom";
import { employeeLogin } from "./loginSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { motion } from "framer-motion";
import { CiStethoscope } from "react-icons/ci";
import { BiHeart, BiShield } from "react-icons/bi";
import { CgArrowRight } from "react-icons/cg";
import IconComponent from "../../components/common/IconComponent";

const LoginNew: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loading = useAppSelector((state) => state.login.loading);

  const handleSubmit = async (values: any) => {
    const data: any = {
      UserName: values.UserName,
      Password: values.Password,
    };
    const response = await dispatch(employeeLogin(data));
    if (response?.meta?.requestStatus === "fulfilled") {
      navigate("/");
    }
  };

  return (
    <div className="login-container-healthgo">
      {/* Animated gradient background */}
      <div className="bg-gradient-animation"></div>

      {/* Decorative elements */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <div className="login-grid">
        {/* Left Side - Brand Section */}
        <motion.div
          className="login-brand-section"
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="brand-content">
            <div className="brand-icon">
              <IconComponent icon={CiStethoscope} />
            </div>
            <h1 className="brand-title">
              Health<span>Go</span>
            </h1>
            <p className="brand-subtitle">Your Health, Our Priority</p>
            <div className="brand-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <IconComponent icon={BiHeart} />
                </div>
                <span>24/7 Healthcare Services</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <IconComponent icon={BiShield} />
                </div>
                <span>Verified Doctors</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <IconComponent icon={CgArrowRight} />
                </div>
                <span>Instant Appointments</span>
              </div>
            </div>
            <div className="brand-stats">
              <div className="stat">
                <h4>25K+</h4>
                <p>Expert Doctors</p>
              </div>
              <div className="stat">
                <h4>5L+</h4>
                <p>Happy Patients</p>
              </div>
              <div className="stat">
                <h4>150+</h4>
                <p>Hospitals</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div
          className="login-form-section"
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="form-wrapper">
            <div className="form-header">
              <span className="logo-text">HealthGo</span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                Welcome Back!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                Log in to access your admin dashboard
              </motion.p>
            </div>

            <Form
              onSubmit={handleSubmit}
              initialValues={{
                UserName: "",
                Password: "",
                RememberMe: false,
              }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement className="login-form">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <Field
                      name={"UserName"}
                      type={"text"}
                      component={FormTextField}
                      label={"Username"}
                      placeholder="Enter your username"
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e) => {
                        formRenderProps.onChange("UserName", {
                          value: e.value,
                        });
                      }}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter" && formRenderProps.allowSubmit) {
                          formRenderProps.onSubmit(e);
                        }
                      }}
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                  >
                    <Field
                      name={"Password"}
                      type={"password"}
                      component={FormPasswordField}
                      label={"Password"}
                      placeholder="Enter your password"
                      astrike={true}
                      validator={passwordValidator}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter" && formRenderProps.allowSubmit) {
                          formRenderProps.onSubmit(e);
                        }
                      }}
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.5 }}
                  >
                    <ButtonWithLoading
                      label="Sign In"
                      themeColor="primary"
                      className="login-button"
                      type="submit"
                      width="100%"
                      style={{
                        borderRadius: 8,
                        height: 48,
                        fontSize: 16,
                        fontWeight: 600,
                        marginTop: 30,
                      }}
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                  </motion.div>
                </FormElement>
              )}
            />

            <motion.div
              className="form-footer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <p>© {new Date().getFullYear()} HealthGo. All rights reserved.</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginNew;
